import React, { useState, useEffect, useCallback } from 'react';
import '../pages/CariHesaplarV2Page.css';
import { toValidObjectIdString } from '../utils/security';
import {
  fetchCariHareketler,
  fetchCariBakiye,
  cariOdemeEkle,
  cariTahsilatEkle,
  cariHareketIptal,
} from '../services/cariV2Service';
import { fetchSiparisDetay } from '../services/siparisService';
import { fetchHakedisDetay } from '../services/hakedisService';
import OdemeEkleModal from './OdemeEkleModal';
import CariHareketDateFilter from './CariHareketDateFilter';
import CariOdemeTahsilatDetayModal from './CariOdemeTahsilatDetayModal';
import SiparisDetayModal from './SiparisDetayModal';
import HakedisDetayModal from './HakedisDetayModal';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import TableListFooter from './TableListFooter';
import { normalizeSayfalama } from '../utils/pagination';
import { formatSignedBalance } from '../utils/formatBalance';
import { computeCariBalanceDisplay } from '../utils/cariBalanceDisplay';

const formatDateTime = (dateValue) => {
  if (!dateValue) return '—';
  try {
    const date = typeof dateValue === 'string' ? new Date(dateValue) : dateValue;
    if (!date || isNaN(date.getTime())) return '—';
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '—';
  }
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '—';
  return `${parseFloat(amount).toFixed(2)} ₺`;
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const getHareketId = (h) => toValidObjectIdString(h?.id ?? h?._id) || '';

const isOdemeTahsilatHareketi = (h) => ['Ödeme', 'Tahsilat'].includes(h?.hareketTipi);

/**
 * Teknik servise ait cari hareket listesi, bakiye, ödeme/tahsilat ekleme.
 * AP Cari Hareketler detay sayfası ve Teknik Servis formu sekmesinde kullanılır.
 */
const CariHareketlerPanel = ({
  teknikServisId,
  teknikServisAdi: teknikServisAdiProp,
  embedded = false,
  isTechView = false,
  active = true,
  onBack,
}) => {
  const { confirm } = useConfirmDialog();
  const validId = toValidObjectIdString(teknikServisId)
    || (typeof teknikServisId === 'string' && /^[0-9a-fA-F]{24}$/.test(teknikServisId) ? teknikServisId : null);

  const [hareketler, setHareketler] = useState([]);
  const [bakiye, setBakiye] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [siparisDetayModal, setSiparisDetayModal] = useState(null);
  const [hakedisDetayModal, setHakedisDetayModal] = useState(null);
  const [odemeTahsilatDetayModal, setOdemeTahsilatDetayModal] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [iptalLoading, setIptalLoading] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({ total: 0, totalPages: 1 });
  const [dateRange, setDateRange] = useState({});

  const loadHareketler = useCallback(async () => {
    if (!validId) return;
    setDetailLoading(true);
    try {
      const response = await fetchCariHareketler(validId, {
        sayfa: currentPage,
        limit: 50,
        baslangicTarihi: dateRange.baslangicTarihi,
        bitisTarihi: dateRange.bitisTarihi,
      });
      if (response?.basarili && response?.veri) {
        setHareketler(response.veri.hareketler || []);
        setPaginationMeta(normalizeSayfalama(response.veri.sayfalama));
      }
    } catch {
      setHareketler([]);
    } finally {
      setDetailLoading(false);
    }
  }, [validId, currentPage, dateRange.baslangicTarihi, dateRange.bitisTarihi]);

  const loadBakiye = useCallback(async () => {
    if (!validId) return;
    try {
      const response = await fetchCariBakiye(validId);
      if (response?.basarili && response?.veri) {
        setBakiye(response.veri);
      }
    } catch {
      setBakiye(null);
    }
  }, [validId]);

  useEffect(() => {
    if (!validId || !active) return;
    loadHareketler();
    loadBakiye();
  }, [validId, active, loadHareketler, loadBakiye]);

  useEffect(() => {
    setCurrentPage(1);
  }, [validId, dateRange.baslangicTarihi, dateRange.bitisTarihi]);

  const handleModalSuccess = async (formData) => {
    if (!validId) return;
    setSubmitting(true);
    try {
      const fn = modalType === 'tahsilat' ? cariTahsilatEkle : cariOdemeEkle;
      const label = modalType === 'tahsilat' ? 'Tahsilat' : 'Ödeme';
      const response = await fn(validId, formData);
      if (response?.basarili) {
        window.showNotification?.('success', `${label} kaydedildi`);
        setModalType(null);
        loadHareketler();
        loadBakiye();
      } else {
        window.showNotification?.('error', response?.mesaj || `${label} kaydedilemedi`);
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'İşlem kaydedilemedi'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleViewSiparis = async (siparisId) => {
    const sid = toValidObjectIdString(siparisId);
    if (!sid) {
      window.showNotification?.('error', 'Geçersiz sipariş ID formatı.');
      return;
    }
    try {
      const response = await fetchSiparisDetay(sid);
      if (response?.basarili && response?.veri) {
        setSiparisDetayModal(response.veri);
      } else {
        window.showNotification?.('error', response?.mesaj || 'Sipariş detayı alınamadı');
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Sipariş detayı yüklenemedi'));
    }
  };

  const handleViewHakedis = async (hakedisId) => {
    const hid = toValidObjectIdString(hakedisId);
    if (!hid) {
      window.showNotification?.('error', 'Geçersiz hakediş ID formatı.');
      return;
    }
    try {
      const response = await fetchHakedisDetay(hid);
      if (response?.basarili && response?.veri?.hakedis) {
        setHakedisDetayModal(response.veri.hakedis);
      } else {
        window.showNotification?.('error', response?.mesaj || 'Hakediş detayı alınamadı');
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Hakediş detayı yüklenemedi'));
    }
  };

  const handleHareketIptal = async (hareket) => {
    if (!hareket.iptalEdilebilir || isTechView) return;
    const ok = await confirm({
      title: 'Hareketi iptal et',
      message: 'Bu hareketi iptal etmek istediğinize emin misiniz? Ters hareket oluşturulacaktır.',
      variant: 'danger',
      confirmLabel: 'Evet, iptal et',
    });
    if (!ok) return;
    const hareketId = getHareketId(hareket);
    if (!hareketId) {
      window.showNotification?.('error', 'Geçersiz hareket ID.');
      return;
    }
    setIptalLoading(hareketId);
    try {
      const response = await cariHareketIptal(hareketId);
      if (response?.basarili) {
        window.showNotification?.('success', 'Hareket iptal edildi');
        loadHareketler();
        loadBakiye();
      } else {
        window.showNotification?.('error', response?.mesaj || 'İptal başarısız');
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'İptal başarısız'));
    } finally {
      setIptalLoading(null);
    }
  };

  const getSignInfo = (h) => {
    const aYon = h?.adminYon ?? 0;
    // Tek görünüm: Admin+ (Hakediş/İade/Tahsilat) +, Admin− (Sipariş/Ödeme/Bakım) −
    return aYon > 0
      ? { sign: '+', cls: 'odeme-tutar' }
      : { sign: '-', cls: 'cari-tutar' };
  };

  const getBadgeCls = (h) => {
    if (h?.hareketTipi === 'Bakım') return 'warning';
    const { cls } = getSignInfo(h);
    return cls === 'odeme-tutar' ? 'success' : 'error';
  };

  if (!validId) {
    return (
      <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-gray)' }}>
        Geçersiz teknik servis.
      </div>
    );
  }

  const displayName = bakiye?.teknikServisAdi || teknikServisAdiProp || 'Teknik Servis';
  const balanceDisplay = computeCariBalanceDisplay(
    bakiye?.adminArti ?? 0,
    bakiye?.adminEksi ?? 0,
    isTechView,
  );
  const showAdminActions = !isTechView;

  const content = (
    <>
      {embedded ? (
        <div className="cari-hareketler-panel-embedded-header">
          <div>
            <div className="section-title" style={{ marginBottom: 4 }}>Cari Hareketler</div>
            <p style={{ fontSize: '14px', color: 'var(--text-gray)', margin: 0, lineHeight: 1.5 }}>
              Bu teknik servise ait cari hareketler. Ödeme ve tahsilat işlemlerini buradan da yapabilirsiniz.
            </p>
          </div>
          {showAdminActions && (
            <div className="cari-hareketler-panel-actions">
              <button type="button" className="primary-btn" onClick={() => setModalType('odeme')}>
                <i className="fas fa-money-bill-wave" aria-hidden />
                Ödeme Ekle
              </button>
              <button
                type="button"
                className="primary-btn cari-tahsilat-btn"
                onClick={() => setModalType('tahsilat')}
              >
                <i className="fas fa-hand-holding-usd" aria-hidden />
                Tahsilat Ekle
              </button>
            </div>
          )}
        </div>
      ) : (
        <div className="content-header cari-detail-header">
          <div className="cari-detail-title">
            {onBack && (
              <button type="button" className="btn btn-secondary" onClick={onBack}>
                <i className="fas fa-arrow-left"></i>
                Geri
              </button>
            )}
            <div>
              <h1>Cari Hareketler - {displayName}</h1>
            </div>
          </div>
          {showAdminActions && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button type="button" className="btn btn-primary" onClick={() => setModalType('odeme')}>
                <i className="fas fa-money-bill-wave"></i>
                Ödeme Ekle
              </button>
              <button
                type="button"
                className="btn btn-success"
                onClick={() => setModalType('tahsilat')}
                style={{ background: 'var(--success-green)', borderColor: 'var(--success-green)' }}
              >
                <i className="fas fa-hand-holding-usd"></i>
                Tahsilat Ekle
              </button>
            </div>
          )}
        </div>
      )}

      {bakiye && (
        <div className="cari-bakiye-card">
          <div className="cari-bakiye-row">
            <span className="cari-bakiye-label">Borç</span>
            <span className="cari-bakiye-value negative">
              {formatSignedBalance(balanceDisplay.borc, formatCurrency)}
            </span>
          </div>
          <div className="cari-bakiye-row">
            <span className="cari-bakiye-label">Alacak</span>
            <span className="cari-bakiye-value positive">
              {formatSignedBalance(balanceDisplay.alacak, formatCurrency)}
            </span>
          </div>
          <div className="cari-bakiye-row cari-bakiye-row-net">
            <span className="cari-bakiye-label">Bakiye</span>
            <span
              className={`cari-bakiye-value ${
                balanceDisplay.bakiye < -0.004
                  ? 'negative'
                  : balanceDisplay.bakiye > 0.004
                    ? 'positive'
                    : ''
              }`}
            >
              {formatSignedBalance(balanceDisplay.bakiye, formatCurrency)}
            </span>
          </div>
        </div>
      )}

      <div className={`card cari-hareket-card ${embedded ? 'cari-hareket-card-embedded' : ''}`}>
        <CariHareketDateFilter
          value={dateRange}
          onChange={setDateRange}
          disabled={detailLoading}
        />
        {detailLoading ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>Yükleniyor...</div>
        ) : hareketler.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-gray)' }}>
            {dateRange.baslangicTarihi || dateRange.bitisTarihi
              ? 'Seçilen tarih aralığında hareket bulunamadı'
              : 'Hareket bulunamadı'}
          </div>
        ) : (
          <>
            <div className="table-container">
              <table className="admins-table">
                <thead>
                  <tr>
                    <th>Tarih</th>
                    <th>Tip</th>
                    <th>Açıklama</th>
                    <th style={{ textAlign: 'right' }}>Tutar</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {hareketler.map((h) => {
                    const { sign, cls } = getSignInfo(h);
                    return (
                      <tr
                        key={getHareketId(h) || `row-${h.olusturmaTarihi}-${h.tutar}`}
                        style={h.iptalMi ? { opacity: 0.5 } : undefined}
                      >
                        <td>{formatDateTime(h.olusturmaTarihi)}</td>
                        <td>
                          <span className={`status-badge ${getBadgeCls(h)}`}>
                            {h.hareketTipi}
                          </span>
                        </td>
                        <td>
                          {h.aciklama || '—'}
                          {h.randevuNumarasi && h.hareketTipi === 'Bakım' ? (
                            <span style={{ marginLeft: '6px', fontSize: '12px', color: 'var(--text-gray)' }}>
                              ({h.randevuNumarasi})
                            </span>
                          ) : null}
                          {h.hakedisNumarasi ? (
                            <span style={{ marginLeft: '6px', fontSize: '12px', color: 'var(--text-gray)' }}>
                              ({h.hakedisNumarasi})
                            </span>
                          ) : null}
                        </td>
                        <td style={{ textAlign: 'right' }} className={cls}>
                          {sign}{formatCurrency(h.tutar)}
                        </td>
                        <td className="cari-islemler-cell">
                          {isOdemeTahsilatHareketi(h) ? (
                            <button
                              type="button"
                              className="category-action-btn edit-btn"
                              onClick={() => setOdemeTahsilatDetayModal(h)}
                              title={`${h.hareketTipi} detayı`}
                            >
                              <i className="fas fa-info-circle"></i>
                            </button>
                          ) : null}
                          {h.siparisId ? (
                            <button
                              type="button"
                              className="category-action-btn edit-btn"
                              onClick={() => handleViewSiparis(h.siparisId)}
                              title="Sipariş detayı"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                          ) : null}
                          {h.hakedisId ? (
                            <button
                              type="button"
                              className="category-action-btn edit-btn"
                              onClick={() => handleViewHakedis(h.hakedisId)}
                              title="Hakediş detayı"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                          ) : null}
                          {h.iptalEdilebilir && showAdminActions ? (
                            <button
                              type="button"
                              className="category-action-btn delete-btn"
                              onClick={() => handleHareketIptal(h)}
                              title="İptal Et"
                              disabled={iptalLoading === getHareketId(h)}
                            >
                              <i className="fas fa-times"></i>
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <TableListFooter
              totalRecords={paginationMeta.total}
              currentPage={currentPage}
              totalPages={paginationMeta.totalPages || 1}
              onPageChange={setCurrentPage}
              loading={detailLoading}
              embedded
            />
          </>
        )}
      </div>

      {modalType && (
        <OdemeEkleModal
          type={modalType}
          teknikServisAdi={displayName}
          onClose={() => setModalType(null)}
          onSuccess={handleModalSuccess}
          isSubmitting={submitting}
        />
      )}

      {siparisDetayModal && (
        <SiparisDetayModal
          siparis={siparisDetayModal}
          onClose={() => setSiparisDetayModal(null)}
          onRefresh={() => { loadHareketler(); loadBakiye(); }}
          isAdmin={!isTechView}
        />
      )}

      {hakedisDetayModal && (
        <HakedisDetayModal
          hakedis={hakedisDetayModal}
          onClose={() => setHakedisDetayModal(null)}
          onRefresh={() => { loadHareketler(); loadBakiye(); }}
        />
      )}

      {odemeTahsilatDetayModal && (
        <CariOdemeTahsilatDetayModal
          hareket={odemeTahsilatDetayModal}
          teknikServisAdi={displayName}
          onClose={() => setOdemeTahsilatDetayModal(null)}
        />
      )}
    </>
  );

  if (embedded) {
    return <div className="cari-hareketler-panel-embedded">{content}</div>;
  }

  return <main className="main-content">{content}</main>;
};

export default CariHareketlerPanel;
